import { Platform } from 'react-native';
import { post } from '~shared/helpers/apiHelper';
import { sendAnalyticsEvent } from '~shared/lib/analytics/src/actions';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { createEvent } from '~shared/lib/analytics/src/helper';
import { getDeviceInfo } from '~shared/lib/devices/deviceInfo';
import { PushNotification } from '~shared/lib/push-notifications';
import { type APIError, type Device, type ReduxDispatch } from '~shared/types';

export type RegisterDevicePayload = {
  id: Device['id'];
  push_token: Device['push_token'];
};

export const REGISTERING_DEVICE = 'REGISTERING_DEVICE';
export type ActionRegisteringDevice = {
  type: typeof REGISTERING_DEVICE;
  payload: {
    push_token: Device['push_token'];
  };
};
export function registeringDevice(push_token: Device['push_token']): ActionRegisteringDevice {
  return {
    type: REGISTERING_DEVICE,
    payload: {
      push_token,
    },
  };
}

export const REGISTER_DEVICE_FAILED = 'REGISTER_DEVICE_FAILED';
export type ActionRegisterDeviceFailed = {
  type: typeof REGISTER_DEVICE_FAILED;
  error: true;
  payload: APIError;
};
export function registerDeviceFailed(payload: APIError): ActionRegisterDeviceFailed {
  return {
    type: REGISTER_DEVICE_FAILED,
    error: true,
    payload,
  };
}

type RegisterDeviceSucceededPayload = {
  push_token: Device['push_token'];
  id: Device['id'];
};
export const REGISTER_DEVICE_SUCCEEDED = 'REGISTER_DEVICE_SUCCEEDED';
export type ActionRegisterDeviceSucceeded = {
  type: typeof REGISTER_DEVICE_SUCCEEDED;
  payload: RegisterDevicePayload;
};
export function registerDeviceSucceeded(payload: RegisterDeviceSucceededPayload): ActionRegisterDeviceSucceeded {
  return {
    type: REGISTER_DEVICE_SUCCEEDED,
    payload,
  };
}

/**
 * @deprecated Redux networking action.
 * @see https://www.notion.so/alto/Guidebook-Migrating-Redux-Networking-Actions-To-React-Query-8567e05fc96c46fcb8020595f24b0edc
 */
export function registerDevice(push_token: Device['push_token']) {
  return async (dispatch: ReduxDispatch) => {
    const deviceInfo = await getDeviceInfo();
    const payload = {
      info: deviceInfo,
      os: Platform.OS,
      push_token,
      device_info_id: deviceInfo.uniqueId,
    };

    dispatch(registeringDevice(push_token));
    const { error, ...res } = await post('/users/register_device', payload);

    if (error) {
      // if we had a conflict, we're actually good. Just pretend we succeeded.
      // This shouldn't be possible, but … just in case.
      if (error.details.statusCode === 409) {
        // res will contain and id in addition to error
        dispatch(
          registerDeviceSucceeded({
            push_token,
            id: error.details.id,
          }),
        );
        return true;
      }

      dispatch(registerDeviceFailed(error));
      return false;
    }

    if (res?.record_created) {
      PushNotification.checkPermissions((permissions) => {
        dispatch(
          sendAnalyticsEvent(
            createEvent(EVENTS.PUSH_NOTIFICATIONS_REQUESTED_APP_LAUNCH, {
              permission_granted: !!(permissions.alert || permissions.badge || permissions.sound),
            }),
          ),
        );
      });
    }

    // res will just contain an id
    dispatch(
      registerDeviceSucceeded({
        push_token,
        ...res,
      }),
    );
    return true;
  };
}
